import React, { useEffect, useState } from "react";
import { Modal, Input, Button, notification } from "antd";
import { useTranslation } from "react-i18next";
import apiClient from "utilities/apiClient";
import NotesThread from "./NotesThread";

/**
 * Notes modal with a thread (author/timestamp history) + an add box.
 * Appends to notes_log via the API while keeping the legacy `notes` string
 * in sync, so older consumers (queue tooltip, exports) keep working.
 */

const NotesModal = ({ open, onClose, documentId, notesLog, legacyNote, onSaved }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState(Array.isArray(notesLog) ? notesLog : []);

  useEffect(() => {
    setEntries(Array.isArray(notesLog) ? notesLog : []);
  }, [notesLog, open]);

  const handleAdd = async () => {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    try {
      const response = await apiClient.post(`/document/note/${documentId}`, {
        notes: text,
        author_id: sessionStorage.getItem("user_id"),
        author_name: sessionStorage.getItem("user_name"),
      });
      const updatedLog = response?.result?.notes_log;
      if (Array.isArray(updatedLog)) {
        setEntries(updatedLog);
        onSaved && onSaved(text, updatedLog);
      }
      setDraft("");
      notification.success({ message: t("noteSaved") });
    } catch (err) {
      notification.error({
        message: t("noteNotSaved"),
        description: t("genericError"),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t("notesLog")}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      className="notes-modal"
      width={520}
    >
      <NotesThread notesLog={entries} legacyNote={legacyNote} />
      <div className="mt-3">
        <Input.TextArea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t("writeNote")}
          autoSize={{ minRows: 3, maxRows: 6 }}
          onPressEnter={(e) => {
            if (e.ctrlKey || e.metaKey) handleAdd();
          }}
        />
        <div className="d-flex justify-content-end mt-2">
          <Button
            type="primary"
            onClick={handleAdd}
            loading={saving}
            disabled={!draft.trim()}
          >
            {t("addNote")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default NotesModal;
